import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { formatMoney } from '@/lib/utils';

type Line = { id: number; code: string; name: string };
type Tier = { id: number; threshold: string; giftName: string };
type Group = { id: number; name: string; lines: Line[]; tiers: Tier[] };

/**
 * Déclaration des cadeaux. Un cadeau = un nom + un seuil d'achat + le FOURNISSEUR
 * concerné. Le budget d'un client est le cumul de ses achats sur les lignes du
 * fournisseur (partagé entre les cadeaux du fournisseur) ; il peut prendre des
 * cadeaux, le même plusieurs fois, tant que le budget restant couvre leur seuil.
 *
 * Le regroupement des LIGNES sous un fournisseur se fait dans la section Lignes ;
 * ici on ne fait que déclarer les cadeaux.
 */
export default function Rewards() {
  const qc = useQueryClient();
  const groups = useQuery<{ groups: Group[] }>({
    queryKey: ['admin', 'rewards'],
    queryFn: () => api('/api/admin/rewards'),
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin', 'rewards'] });

  const [giftName, setGiftName] = useState('');
  const [threshold, setThreshold] = useState('');
  const [supplierIds, setSupplierIds] = useState<number[]>([]);
  // Tri de l'affichage des cadeaux déclarés (côté écran).
  const [sortBy, setSortBy] = useState<'fournisseur' | 'cadeau'>('fournisseur');

  const list = groups.data?.groups ?? [];

  const addGift = useMutation({
    // Un cadeau offert par PLUSIEURS fournisseurs = créé dans chacun (chaque
    // fournisseur garde son propre budget). Budget partagé côté fournisseur inchangé.
    mutationFn: async () => {
      await Promise.all(
        supplierIds.map((id) =>
          api(`/api/admin/rewards/groups/${id}/tiers`, {
            method: 'POST',
            body: JSON.stringify({ threshold: Number(threshold), giftName: giftName.trim() }),
          }),
        ),
      );
    },
    onSuccess: () => {
      setGiftName('');
      setThreshold('');
      setSupplierIds([]);
      void invalidate();
    },
  });
  const delGift = useMutation({
    mutationFn: (tierId: number) => api(`/api/admin/rewards/tiers/${tierId}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
  // Rattacher un cadeau (nom + seuil) à UN fournisseur — sert dans les deux sens :
  // ajouter un fournisseur à un cadeau existant, ou un cadeau existant à un fournisseur.
  const addTier = useMutation({
    mutationFn: (v: { groupId: number; threshold: number; giftName: string }) =>
      api(`/api/admin/rewards/groups/${v.groupId}/tiers`, {
        method: 'POST',
        body: JSON.stringify({ threshold: v.threshold, giftName: v.giftName }),
      }),
    onSuccess: invalidate,
  });
  // Ajout EN LOT : « tout ajouter » (tous les cadeaux d'un fournisseur, ou tous les
  // fournisseurs d'un cadeau).
  const addTierMany = useMutation({
    mutationFn: (items: { groupId: number; threshold: number; giftName: string }[]) =>
      Promise.all(
        items.map((v) =>
          api(`/api/admin/rewards/groups/${v.groupId}/tiers`, {
            method: 'POST',
            body: JSON.stringify({ threshold: v.threshold, giftName: v.giftName }),
          }),
        ),
      ),
    onSuccess: invalidate,
  });
  const ajoutEnCours = addTier.isPending || addTierMany.isPending;

  const toggleSupplier = (id: number) =>
    setSupplierIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  const allSelected = list.length > 0 && supplierIds.length === list.length;
  const toggleAll = () => setSupplierIds(allSelected ? [] : list.map((g) => g.id));
  const canAdd = giftName.trim() !== '' && Number(threshold) > 0 && supplierIds.length > 0;

  // Vues triées. « Par fournisseur » : sections triées A→Z, cadeaux internes triés
  // par nom. « Par cadeau » : liste à plat de tous les cadeaux, triée par nom (le
  // même cadeau apparaît une fois par fournisseur qui l'offre).
  const totalGifts = list.reduce((n, g) => n + g.tiers.length, 0);
  const groupsSorted = [...list].sort((a, b) => a.name.localeCompare(b.name));
  const giftRows = list
    .flatMap((g) => g.tiers.map((t) => ({ ...t, groupName: g.name, groupId: g.id })))
    .sort(
      (a, b) =>
        a.giftName.localeCompare(b.giftName) ||
        a.groupName.localeCompare(b.groupName) ||
        Number(a.threshold) - Number(b.threshold),
    );
  // Regroupé par NOM de cadeau : une boîte par cadeau, listant les fournisseurs qui
  // l'offrent (+ leur seuil). Boîtes triées par seuil croissant (le plus bas d'abord),
  // départage par nom.
  const giftsByName = Array.from(
    giftRows.reduce((map, t) => {
      (map.get(t.giftName) ?? map.set(t.giftName, []).get(t.giftName)!).push(t);
      return map;
    }, new Map<string, typeof giftRows>()),
  ).sort(([nameA, rowsA], [nameB, rowsB]) => {
    const minA = Math.min(...rowsA.map((r) => Number(r.threshold)));
    const minB = Math.min(...rowsB.map((r) => Number(r.threshold)));
    return minA - minB || nameA.localeCompare(nameB);
  });
  // Cadeaux DISTINCTS (nom + seuil) — pour proposer d'ajouter un cadeau existant
  // à un fournisseur (vue par fournisseur).
  const distinctGifts = Array.from(
    new Map(
      giftRows.map((r) => [`${r.giftName} ${r.threshold}`, { giftName: r.giftName, threshold: r.threshold }]),
    ).values(),
  ).sort((a, b) => a.giftName.localeCompare(b.giftName) || Number(a.threshold) - Number(b.threshold));

  return (
    <div className="space-y-6">
      <p className="text-muted-foreground text-sm">
        Déclarez un cadeau : son <b>nom</b>, le <b>seuil d'achat</b> à atteindre, et le{' '}
        <b>fournisseur</b> concerné. Le budget d'un client est le cumul de ses achats sur les lignes
        du fournisseur ; il peut prendre des cadeaux (le même plusieurs fois) tant que ce budget
        couvre leur seuil. Le rattachement des lignes à un fournisseur se fait dans la section{' '}
        <b>Lignes</b>.
      </p>

      <section className="bg-card rounded-xl border p-6">
        <h2 className="mb-4 text-lg font-semibold">Nouveau cadeau</h2>
        {list.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            Aucun fournisseur pour l'instant. Créez d'abord un fournisseur dans la section{' '}
            <b>Lignes</b>.
          </p>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (canAdd) addGift.mutate();
            }}
            className="space-y-4"
          >
            <div className="flex flex-wrap items-end gap-3">
              <label className="space-y-1.5">
                <span className="block text-sm font-medium">Nom du cadeau</span>
                <input
                  value={giftName}
                  onChange={(e) => setGiftName(e.target.value)}
                  placeholder="Ex. Carte-cadeau 50$"
                  className="border-input bg-background h-11 w-64 rounded-lg border px-3"
                />
              </label>
              <label className="space-y-1.5">
                <span className="block text-sm font-medium">Seuil d'achat ($)</span>
                <input
                  type="number"
                  min={1}
                  step="0.01"
                  value={threshold}
                  onChange={(e) => setThreshold(e.target.value)}
                  placeholder="500"
                  className="border-input bg-background h-11 w-28 rounded-lg border px-3"
                />
              </label>
            </div>

            <fieldset>
              <div className="mb-2 flex items-center gap-3">
                <legend className="text-sm font-medium">Fournisseur(s) qui offrent ce cadeau</legend>
                <button
                  type="button"
                  onClick={toggleAll}
                  className="text-primary text-sm font-medium underline underline-offset-4"
                >
                  {allSelected ? 'Tout désélectionner' : 'Tout sélectionner'}
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {list.map((g) => {
                  const checked = supplierIds.includes(g.id);
                  return (
                    <label
                      key={g.id}
                      className={`cursor-pointer rounded-lg border px-3 py-2 text-sm ${
                        checked ? 'border-primary bg-primary/10 font-medium' : 'border-input'
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="sr-only"
                        checked={checked}
                        onChange={() => toggleSupplier(g.id)}
                      />
                      {g.name}
                    </label>
                  );
                })}
              </div>
            </fieldset>

            <button
              type="submit"
              disabled={!canAdd || addGift.isPending}
              className="bg-primary text-primary-foreground h-11 rounded-lg px-5 font-medium disabled:opacity-40"
            >
              {addGift.isPending ? 'Ajout…' : 'Ajouter le cadeau'}
            </button>
          </form>
        )}
        {addGift.isError && (
          <p className="text-destructive mt-3 text-sm font-medium">
            {(addGift.error as Error).message}
          </p>
        )}
      </section>

      {list.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-lg font-semibold">Cadeaux déclarés ({totalGifts})</h2>
          <label className="text-muted-foreground flex items-center gap-1.5 text-sm">
            Trier par
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
              className="border-input bg-background h-10 rounded-lg border px-2 text-sm"
            >
              <option value="fournisseur">Fournisseur</option>
              <option value="cadeau">Cadeau</option>
            </select>
          </label>
        </div>
      )}

      {sortBy === 'fournisseur' ? (
        // Regroupé par fournisseur (sections triées A→Z, cadeaux internes par nom).
        groupsSorted.map((g) => (
          <section key={g.id} className="bg-card rounded-xl border p-6">
            <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
              <h3 className="text-lg font-semibold">{g.name}</h3>
              <span className="text-muted-foreground text-sm">
                {g.lines.length === 0
                  ? 'Aucune ligne rattachée — à faire dans la section Lignes.'
                  : 'Lignes : ' + g.lines.map((l) => l.code).join(', ')}
              </span>
            </div>
            {g.tiers.length === 0 ? (
              <p className="text-muted-foreground text-sm">Aucun cadeau pour ce fournisseur.</p>
            ) : (
              <ul className="divide-y rounded-lg border">
                {[...g.tiers]
                  .sort(
                    (a, b) =>
                      Number(a.threshold) - Number(b.threshold) ||
                      a.giftName.localeCompare(b.giftName),
                  )
                  .map((t) => (
                    <li key={t.id} className="flex items-center justify-between gap-3 px-3 py-2">
                      <span className="w-24 font-semibold tabular-nums">
                        {formatMoney(t.threshold)}
                      </span>
                      <span className="flex-1">{t.giftName}</span>
                      <button
                        onClick={() => delGift.mutate(t.id)}
                        className="text-destructive text-sm font-medium"
                      >
                        Retirer
                      </button>
                    </li>
                  ))}
              </ul>
            )}
            {/* Ajouter à CE fournisseur un cadeau déjà déclaré ailleurs. */}
            <AjoutSelect
              placeholder="+ Ajouter un cadeau existant…"
              pending={ajoutEnCours}
              options={distinctGifts
                .filter(
                  (dg) =>
                    !g.tiers.some((t) => t.giftName === dg.giftName && t.threshold === dg.threshold),
                )
                .map((dg) => ({
                  key: `${dg.giftName} ${dg.threshold}`,
                  label: `${dg.giftName} — ${formatMoney(dg.threshold)}`,
                  data: dg,
                }))}
              onAdd={(o) =>
                addTier.mutate({
                  groupId: g.id,
                  threshold: Number(o.data.threshold),
                  giftName: o.data.giftName,
                })
              }
              onAddAll={(opts) =>
                addTierMany.mutate(
                  opts.map((o) => ({
                    groupId: g.id,
                    threshold: Number(o.data.threshold),
                    giftName: o.data.giftName,
                  })),
                )
              }
            />
          </section>
        ))
      ) : giftsByName.length === 0 ? (
        <section className="bg-card rounded-xl border p-6">
          <p className="text-muted-foreground text-sm">Aucun cadeau déclaré pour l'instant.</p>
        </section>
      ) : (
        // Une boîte par nom de cadeau. Le seuil est le même pour tous les fournisseurs
        // d'un cadeau (déclaration commune) → affiché une seule fois dans l'en-tête ;
        // les lignes ne listent que les fournisseurs. Cas rare de seuils différents :
        // repli sur un seuil par ligne.
        <div className="grid gap-4 md:grid-cols-2">
          {giftsByName.map(([name, rows]) => {
            const seuilUnique =
              new Set(rows.map((r) => r.threshold)).size === 1 ? rows[0]!.threshold : null;
            return (
              <section key={name} className="bg-card rounded-xl border p-5">
                <div className="mb-3 flex items-baseline justify-between gap-2">
                  <h3 className="text-lg font-semibold">{name}</h3>
                  {seuilUnique != null ? (
                    <span className="font-semibold tabular-nums">
                      Seuil&nbsp;: {formatMoney(seuilUnique)}
                    </span>
                  ) : (
                    <span className="text-muted-foreground text-sm">seuils variables</span>
                  )}
                </div>
                <ul className="divide-y rounded-lg border">
                  {rows.map((t) => (
                    <li key={t.id} className="flex items-center justify-between gap-3 px-3 py-2">
                      <span className="flex-1 truncate" title={t.groupName}>
                        {t.groupName}
                      </span>
                      {seuilUnique == null && (
                        <span className="w-24 text-right font-semibold tabular-nums">
                          {formatMoney(t.threshold)}
                        </span>
                      )}
                      <button
                        onClick={() => delGift.mutate(t.id)}
                        className="text-destructive text-sm font-medium"
                      >
                        Retirer
                      </button>
                    </li>
                  ))}
                </ul>
                {/* Ajouter un fournisseur à CE cadeau (même seuil). */}
                <AjoutSelect
                  placeholder="+ Ajouter un fournisseur…"
                  pending={ajoutEnCours}
                  options={list
                    .filter((g) => !rows.some((r) => r.groupId === g.id))
                    .map((g) => ({ key: String(g.id), label: g.name, data: g }))}
                  onAdd={(o) =>
                    addTier.mutate({
                      groupId: o.data.id,
                      threshold: Number(seuilUnique ?? rows[0]!.threshold),
                      giftName: name,
                    })
                  }
                  onAddAll={(opts) =>
                    addTierMany.mutate(
                      opts.map((o) => ({
                        groupId: o.data.id,
                        threshold: Number(seuilUnique ?? rows[0]!.threshold),
                        giftName: name,
                      })),
                    )
                  }
                />
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Petit sélecteur « + Ajouter » (+ « Tout ajouter »), masqué s'il n'y a rien à ajouter. */
function AjoutSelect<T>({
  placeholder,
  options,
  onAdd,
  onAddAll,
  pending,
}: {
  placeholder: string;
  options: { key: string; label: string; data: T }[];
  onAdd: (o: { key: string; label: string; data: T }) => void;
  onAddAll?: (opts: { key: string; label: string; data: T }[]) => void;
  pending: boolean;
}) {
  const [sel, setSel] = useState('');
  if (options.length === 0) return null;
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2">
      <select
        value={sel}
        onChange={(e) => setSel(e.target.value)}
        className="border-input bg-background h-9 max-w-full rounded-lg border px-2 text-sm"
      >
        <option value="">{placeholder}</option>
        {options.map((o) => (
          <option key={o.key} value={o.key}>
            {o.label}
          </option>
        ))}
      </select>
      <button
        type="button"
        disabled={!sel || pending}
        onClick={() => {
          const o = options.find((x) => x.key === sel);
          if (o) onAdd(o);
          setSel('');
        }}
        className="border-primary/40 text-primary rounded-lg border px-3 py-1.5 text-sm font-medium disabled:opacity-40"
      >
        Ajouter
      </button>
      {onAddAll && options.length > 1 && (
        <button
          type="button"
          disabled={pending}
          onClick={() => {
            onAddAll(options);
            setSel('');
          }}
          className="bg-primary text-primary-foreground rounded-lg px-3 py-1.5 text-sm font-medium disabled:opacity-40"
        >
          Tout ajouter ({options.length})
        </button>
      )}
    </div>
  );
}
